import React, { useState, useEffect } from 'react';
import { 
  Building2, Users, MessageSquare, Globe, Shield, 
  RefreshCw, Plus, Trash2, Edit2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter
} from '../../components/ui/dialog';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../../components/ui/table';
import { useAuth, API } from '../../App';
import { toast } from 'sonner';
import axios from 'axios';

const AdminPage = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Translation form
  const [transDialogOpen, setTransDialogOpen] = useState(false);
  const [transKey, setTransKey] = useState('');
  const [transEn, setTransEn] = useState('');
  const [transEs, setTransEs] = useState('');
  const [transFr, setTransFr] = useState('');
  const [transDe, setTransDe] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, orgsRes, transRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/organizations`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/translations`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStats(statsRes.data);
      setOrganizations(orgsRes.data);
      setTranslations(transRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrg = async (orgId) => {
    if (!window.confirm('Are you sure? This will delete all organization data.')) return;
    
    try {
      await axios.delete(`${API}/organizations/${orgId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Organization deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete organization');
    }
  };

  const handleCreateTranslation = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/translations`, {
        key: transKey,
        translations: {
          en: transEn,
          es: transEs,
          fr: transFr,
          de: transDe
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Translation created');
      setTransDialogOpen(false);
      resetTransForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to create translation');
    }
  };

  const handleDeleteTranslation = async (key) => {
    if (!window.confirm('Delete this translation?')) return;
    
    try {
      await axios.delete(`${API}/translations/${key}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Translation deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete translation');
    }
  };

  const resetTransForm = () => {
    setTransKey('');
    setTransEn('');
    setTransEs('');
    setTransFr('');
    setTransDe('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="w-6 h-6 text-emerald-600" />
          Super Admin Panel
        </h1>
        <p className="text-muted-foreground">Platform-wide management and configuration</p>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="stat-orgs">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_organizations || 0}</p>
                <p className="text-sm text-muted-foreground">Organizations</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-users">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
                <p className="text-sm text-muted-foreground">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-conversations">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_conversations || 0}</p>
                <p className="text-sm text-muted-foreground">Conversations</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-messages">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_messages || 0}</p>
                <p className="text-sm text-muted-foreground">Messages</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="organizations" className="space-y-6">
        <TabsList>
          <TabsTrigger value="organizations" data-testid="admin-tab-orgs">Organizations</TabsTrigger>
          <TabsTrigger value="translations" data-testid="admin-tab-translations">Translations</TabsTrigger>
        </TabsList>

        {/* Organizations Tab */}
        <TabsContent value="organizations">
          <Card>
            <CardHeader>
              <CardTitle>All Organizations</CardTitle>
              <CardDescription>Manage platform organizations</CardDescription>
            </CardHeader>
            <CardContent>
              {organizations.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No organizations yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Industry</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {organizations.map((org) => (
                      <TableRow key={org.id} data-testid={`org-row-${org.id}`}>
                        <TableCell className="font-medium">{org.name}</TableCell>
                        <TableCell>{org.industry || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{org.subscription_plan}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={org.status === 'active' ? 'bg-emerald-600' : 'bg-yellow-500'}>
                            {org.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(org.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteOrg(org.id)}
                            className="text-red-500 hover:text-red-600"
                            data-testid={`delete-org-${org.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Translations Tab */}
        <TabsContent value="translations">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Translation Dictionary
                </CardTitle>
                <CardDescription>Manage multi-language translations</CardDescription>
              </div>
              <Dialog open={transDialogOpen} onOpenChange={setTransDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-translation-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Translation
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add Translation</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateTranslation} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="key">Translation Key</Label>
                      <Input
                        id="key"
                        placeholder="e.g., welcome_message"
                        value={transKey}
                        onChange={(e) => setTransKey(e.target.value)}
                        required
                        data-testid="trans-key-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>English (EN)</Label>
                        <Input
                          value={transEn}
                          onChange={(e) => setTransEn(e.target.value)}
                          required
                          data-testid="trans-en-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Spanish (ES)</Label>
                        <Input
                          value={transEs}
                          onChange={(e) => setTransEs(e.target.value)}
                          data-testid="trans-es-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>French (FR)</Label>
                        <Input
                          value={transFr}
                          onChange={(e) => setTransFr(e.target.value)}
                          data-testid="trans-fr-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>German (DE)</Label>
                        <Input
                          value={transDe}
                          onChange={(e) => setTransDe(e.target.value)}
                          data-testid="trans-de-input"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setTransDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-trans-btn">
                        Create Translation
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {translations.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No translations yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Key</TableHead>
                      <TableHead>EN</TableHead>
                      <TableHead>ES</TableHead>
                      <TableHead>FR</TableHead>
                      <TableHead>DE</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {translations.map((trans) => (
                      <TableRow key={trans.key} data-testid={`trans-row-${trans.key}`}>
                        <TableCell className="font-mono text-sm">{trans.key}</TableCell>
                        <TableCell className="text-sm">{trans.translations?.en || '-'}</TableCell>
                        <TableCell className="text-sm">{trans.translations?.es || '-'}</TableCell>
                        <TableCell className="text-sm">{trans.translations?.fr || '-'}</TableCell>
                        <TableCell className="text-sm">{trans.translations?.de || '-'}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteTranslation(trans.key)}
                            className="text-red-500 hover:text-red-600"
                            data-testid={`delete-trans-${trans.key}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPage;
